/**
 * App Router - Main application router with URL-based routing
 */

import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore, useIsAuthenticated, useAuthTokens } from '@app/core';
import { LoginScreen, SignupScreen } from '@modules/auth';
import { ShopHomeScreen, PartnerHomeScreen, MyShopDashboard, ManageSubscriptionPlans, ManagePolicies, ManagePartners } from '@modules/shop-shell';
import { CatalogListScreen } from '@modules/catalog';
import { ManageShopScreen } from '@modules/manage-shop';
import { ManageUserScreen } from '@modules/manage-user';
import { ManageOrdersScreen } from '@modules/orders';
import { WebsiteBuilderScreen } from '@modules/website-builder';
import { TermsAndConditions } from './pages/TermsAndConditions';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { GrievanceRedressal } from './pages/GrievanceRedressal';

/**
 * Public URLs for policy pages:
 * - Terms & Conditions: /terms-and-conditions
 * - Privacy Policy: /privacy-policy
 * - Grievance Redressal: /grievance-redressal
 */

/**
 * Smart Redirect Component
 * Redirects to appropriate page based on auth status and user role
 */
const SmartRedirect: React.FC = () => {
  const isAuthenticated = useIsAuthenticated();
  const tokens = useAuthTokens();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (tokens?.userRole === 'SUPER_ADMIN') {
    console.log('👑 [SmartRedirect] Redirecting SUPER_ADMIN to dashboard');
    return <Navigate to="/admin/dashboard" replace />;
  }

  if (tokens?.userRole === 'PARTNER') {
    console.log('🤝 [SmartRedirect] Redirecting PARTNER to partner home');
    return <Navigate to="/partner/home" replace />;
  }

  console.log('👤 [SmartRedirect] Redirecting regular user to home');
  return <Navigate to="/home" replace />;
};

/**
 * Protected Route Component
 * Redirects to login if not authenticated
 * Redirects SUPER_ADMIN to dashboard if trying to access regular user pages
 * Redirects PARTNER to partner home if trying to access regular user pages
 */
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useIsAuthenticated();
  const tokens = useAuthTokens();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Prevent SUPER_ADMIN from accessing regular user pages
  if (tokens?.userRole === 'SUPER_ADMIN') {
    console.log('⚠️ [ProtectedRoute] SUPER_ADMIN cannot access regular user pages, redirecting to dashboard');
    return <Navigate to="/admin/dashboard" replace />;
  }

  // Prevent PARTNER from accessing regular user pages
  if (tokens?.userRole === 'PARTNER') {
    console.log('⚠️ [ProtectedRoute] PARTNER cannot access regular user pages, redirecting to partner home');
    return <Navigate to="/partner/home" replace />;
  }

  return <>{children}</>;
};

/**
 * Super Admin Route Component
 * Only allows SUPER_ADMIN role
 */
const SuperAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useIsAuthenticated();
  const tokens = useAuthTokens();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (tokens?.userRole !== 'SUPER_ADMIN') {
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
};

/**
 * Partner Route Component
 * Only allows PARTNER role
 */
const PartnerRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useIsAuthenticated();
  const tokens = useAuthTokens();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (tokens?.userRole !== 'PARTNER') {
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
};

/**
 * Website Builder Route Component
 * Allows both SUPER_ADMIN and regular ADMIN/STAFF users
 * SUPER_ADMIN: Creates default templates for shop types
 * ADMIN/STAFF: Customizes their shop's website
 */
const WebsiteBuilderRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useIsAuthenticated();
  const tokens = useAuthTokens();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Allow SUPER_ADMIN, ADMIN, and STAFF roles
  const allowedRoles = ['SUPER_ADMIN', 'ADMIN', 'STAFF'];
  if (!allowedRoles.includes(tokens?.userRole || '')) {
    console.log('⚠️ [WebsiteBuilderRoute] User role not allowed, redirecting to home');
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
};

/**
 * App Routes Component
     * Handles navigation logic and auth state rehydration
 */
const AppRoutes: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { checkAuthStatus, logout, isAuthenticated } = useAuthStore();
  const tokens = useAuthTokens();
  const [isInitializing, setIsInitializing] = useState(true);

  // Check authentication on mount and wait for rehydration
  useEffect(() => {
    const initializeAuth = async (): Promise<void> => {
      try {
        // Wait a tick to allow Zustand persist to rehydrate from localStorage
        await new Promise(resolve => setTimeout(resolve, 100));
        await checkAuthStatus();
      } finally {
        setIsInitializing(false);
      }
    };
    initializeAuth();
  }, [checkAuthStatus]);

  // Handle routing after auth state is initialized
  useEffect(() => {
    if (isInitializing) return;

    if (isAuthenticated && tokens) {
      const isOnAuthPages = location.pathname === '/login' || location.pathname === '/signup';
      const isOnPublicPolicyPages = ['/terms-and-conditions', '/privacy-policy', '/grievance-redressal'].some(
        path => location.pathname === path
      );
      const isOnRegularUserPages = ['/home', '/catalog', '/manage-shop', '/manage-user'].some(
        path => location.pathname.startsWith(path)
      );
      const isOnAdminDashboard = location.pathname === '/admin/dashboard';
      const isOnPartnerHome = location.pathname === '/partner/home';
      const isOnWebsiteBuilder = location.pathname === '/website-builder';

      // Don't redirect if already on the right page
      if (tokens.userRole === 'SUPER_ADMIN') {
        // Allow SUPER_ADMIN to access website builder for creating default templates
        if (isOnWebsiteBuilder) {
          console.log('🎨 [AppRouter] SUPER_ADMIN accessing website builder');
          // Stay on website builder - do nothing
          return;
        }

        // Redirect SUPER_ADMIN away from auth pages or regular user pages to dashboard
        if (isOnAuthPages || isOnRegularUserPages || isOnPartnerHome) {
          console.log('👑 [AppRouter] SUPER_ADMIN detected, routing to dashboard');
          navigate('/admin/dashboard', { replace: true });
        }
        // SUPER_ADMIN is already on /admin/dashboard or /admin/* - do nothing
      } else if (tokens.userRole === 'PARTNER') {
        // Redirect PARTNER away from auth pages or regular user pages to partner home
        if (isOnAuthPages || isOnRegularUserPages || isOnAdminDashboard) {
          console.log('🤝 [AppRouter] PARTNER detected, routing to partner home');
          navigate('/partner/home', { replace: true });
        }
        // PARTNER is already on /partner/home - do nothing
      } else {
        // Regular user logic
        if (isOnAuthPages && !isOnPublicPolicyPages) {
          // Redirect authenticated regular users from login/signup to home
          console.log('👤 [AppRouter] Regular user on auth page, routing to home');
          navigate('/home', { replace: true });
        } else if (isOnAdminDashboard || isOnPartnerHome) {
          // Prevent regular users from accessing admin dashboard or partner home
          console.log('⚠️ [AppRouter] Regular user cannot access admin/partner pages, routing to home');
          navigate('/home', { replace: true });
        }
        // Regular user is on /home or other regular pages - do nothing
      }
    }
  }, [isInitializing, isAuthenticated, tokens, location.pathname, navigate]);

  // Show loading spinner while initializing to prevent flash of login screen
  if (isInitializing) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#fff'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 40,
            height: 40,
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #007AFF',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ color: '#666', fontSize: 14 }}>Loading...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  const handleLogout = async (): Promise<void> => {
    try {
      console.log('🚪 Logging out user...');
      await logout();
      console.log('✅ Logout successful, redirecting to login page');
      navigate('/login');
    } catch (error) {
      console.error('❌ Logout error:', error);
      navigate('/login');
    }
  };

  const handleLoginSuccess = (): void => {
    // Check the current auth state to determine where to redirect
    const currentTokens = useAuthStore.getState().tokens;
    if (currentTokens?.userRole === 'SUPER_ADMIN') {
      console.log('👑 [AppRouter] Login successful - SUPER_ADMIN, routing to dashboard');
      navigate('/admin/dashboard', { replace: true });
    } else if (currentTokens?.userRole === 'PARTNER') {
      console.log('🤝 [AppRouter] Login successful - PARTNER, routing to partner home');
      navigate('/partner/home', { replace: true });
    } else {
      console.log('👤 [AppRouter] Login successful - Regular user, routing to home');
      navigate('/home', { replace: true });
    }
  };

  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <LoginScreen
            onLoginSuccess={handleLoginSuccess}
            onNavigateToSignup={() => navigate('/signup')}
          />
        }
      />
      <Route
        path="/signup"
        element={
          <SignupScreen
            onSignupSuccess={() => navigate('/login')}
            onNavigateToLogin={() => navigate('/login')}
            onViewTerms={() => navigate('/terms-and-conditions')}
            onViewPrivacy={() => navigate('/privacy-policy')}
            onViewGrievance={() => navigate('/grievance-redressal')}
          />
        }
      />

      {/* Public Policy Pages */}
      <Route
        path="/terms-and-conditions"
        element={<TermsAndConditions onClose={() => navigate('/signup')} />}
      />
      <Route
        path="/privacy-policy"
        element={<PrivacyPolicy onClose={() => navigate('/signup')} />}
      />
      <Route
        path="/grievance-redressal"
        element={<GrievanceRedressal onClose={() => navigate('/signup')} />}
      />

      {/* Super Admin Routes */}
      <Route
        path="/admin/dashboard"
        element={
          <SuperAdminRoute>
            <MyShopDashboard onLogout={handleLogout} />
          </SuperAdminRoute>
        }
      />

      <Route
        path="/admin/subscription-plans"
        element={
          <SuperAdminRoute>
            <ManageSubscriptionPlans />
          </SuperAdminRoute>
        }
      />

      <Route
        path="/admin/policies"
        element={
          <SuperAdminRoute>
            <ManagePolicies />
          </SuperAdminRoute>
        }
      />

      <Route
        path="/admin/partners"
        element={
          <SuperAdminRoute>
            <ManagePartners />
          </SuperAdminRoute>
        }
      />

      {/* Partner Routes */}
      <Route
        path="/partner/home"
        element={
          <PartnerRoute>
            <PartnerHomeScreen onLogout={handleLogout} />
          </PartnerRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/home"
        element={
          <ProtectedRoute>
            <ShopHomeScreen
              onManageShop={() => navigate('/manage-shop')}
              onManageUser={() => navigate('/manage-user')}
              onManageCatalog={() => navigate('/catalog')}
              onManageOrders={() => navigate('/orders')}
              onWebsiteBuilder={() => navigate('/website-builder')}
              onLogout={handleLogout}
            />
          </ProtectedRoute>
        }
      />

      <Route
        path="/catalog"
        element={
          <ProtectedRoute>
            <CatalogListScreen
              onAddItem={() => navigate('/catalog/add')}
              onEditItem={() => navigate('/catalog/edit')}
              onDeleteItem={() => {}}
              onBack={() => navigate('/home')}
              onHome={() => navigate('/home')}
              onManageShop={() => navigate('/manage-shop')}
              onManageUser={() => navigate('/manage-user')}
              onManageOrders={() => navigate('/orders')}
              onWebsiteBuilder={() => navigate('/website-builder')}
              onLogout={handleLogout}
            />
          </ProtectedRoute>
        }
      />

      <Route
        path="/manage-shop"
        element={
          <ProtectedRoute>
            <ManageShopScreen
              onSaveSuccess={() => navigate('/home')}
              onCancel={() => navigate('/home')}
              onHome={() => navigate('/home')}
              onManageUser={() => navigate('/manage-user')}
              onManageCatalog={() => navigate('/catalog')}
              onManageOrders={() => navigate('/orders')}
              onWebsiteBuilder={() => navigate('/website-builder')}
              onLogout={handleLogout}
            />
          </ProtectedRoute>
        }
      />

      <Route
        path="/catalog"
        element={
          <ProtectedRoute>
            <CatalogListScreen
              onAddItem={() => navigate('/catalog/add')}
              onEditItem={() => navigate('/catalog/edit')}
              onHome={() => navigate('/home')}
              onManageShop={() => navigate('/manage-shop')}
              onManageUser={() => navigate('/manage-user')}
              onManageOrders={() => navigate('/orders')}
              onWebsiteBuilder={() => navigate('/website-builder')}
              onLogout={handleLogout}
            />
          </ProtectedRoute>
        }
      />

      <Route
        path="/manage-user"
        element={
          <ProtectedRoute>
            <ManageUserScreen
              onBack={() => navigate('/home')}
              onHome={() => navigate('/home')}
              onManageShop={() => navigate('/manage-shop')}
              onManageCatalog={() => navigate('/catalog')}
              onManageOrders={() => navigate('/orders')}
              onWebsiteBuilder={() => navigate('/website-builder')}
              onLogout={handleLogout}
            />
          </ProtectedRoute>
        }
      />

      <Route
        path="/orders"
        element={
          <ProtectedRoute>
            <ManageOrdersScreen
              onBack={() => navigate('/home')}
              onHome={() => navigate('/home')}
              onManageShop={() => navigate('/manage-shop')}
              onManageCatalog={() => navigate('/catalog')}
              onManageUser={() => navigate('/manage-user')}
              onWebsiteBuilder={() => navigate('/website-builder')}
              onLogout={handleLogout}
            />
          </ProtectedRoute>
        }
      />

      <Route
        path="/website-builder"
        element={
          <WebsiteBuilderRoute>
            <WebsiteBuilderScreen
              onBack={() => navigate('/home')}
              onHome={() => navigate('/home')}
              onManageShop={() => navigate('/manage-shop')}
              onManageCatalog={() => navigate('/catalog')}
              onManageUser={() => navigate('/manage-user')}
              onManageOrders={() => navigate('/orders')}
              onWebsiteBuilder={() => navigate('/website-builder')}
              onLogout={handleLogout}
            />
          </WebsiteBuilderRoute>
        }
      />
      {/* Default redirect - smart redirect based on user role */}
      <Route path="/" element={<SmartRedirect />} />
      <Route path="*" element={<SmartRedirect />} />
    </Routes>
  );
};

export const AppRouter: React.FC = () => {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
};

